import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { format, addDays, isAfter, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Dashboard({ setActive }) {
  const { userData } = useAuth();
  const isAdmin = userData?.role === 'admin';
  const [stats, setStats] = useState({ totalVentas: 0, totalMalaika: 0, totalComisiones: 0, ventasCount: 0 });
  const [alertsStock, setAlertsStock] = useState([]);
  const [proxExperiencias, setProxExperiencias] = useState([]);
  const [recentVentas, setRecentVentas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    let cancelled = false;

    async function load() {
      try {
        // Stock alerts — embajadora ve solo el suyo
        const stockQ = isAdmin
          ? collection(db, 'stock')
          : query(collection(db, 'stock'), where('embajadoraId', '==', userData.uid));
        const stockSnap = await getDocs(stockQ);
        if (cancelled) return;
        const alerts = [];
        stockSnap.forEach(d => {
          const s = d.data();
          if (s.stockDisponible <= s.stockMinimo) alerts.push({ id: d.id, ...s });
        });
        setAlertsStock(alerts.slice(0, 5));

        // Ventas sin orderBy — evita conflicto con serverTimestamp recién creado
        const ventasSnap = await getDocs(collection(db, 'ventas'));
        if (cancelled) return;
        let totalVentas = 0, totalMalaika = 0, totalComisiones = 0;
        const ventas = [];
        ventasSnap.forEach(d => {
          const v = { id: d.id, ...d.data() };
          if (!isAdmin && v.embajadoraId !== userData.uid) return;
          totalVentas += v.precio || 0;
          totalMalaika += v.importeMalaika || 0;
          totalComisiones += v.comision || 0;
          ventas.push(v);
        });
        ventas.sort((a, b) => {
          const fa = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha || 0);
          const fb = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha || 0);
          return fb - fa;
        });
        setStats({ totalVentas, totalMalaika, totalComisiones, ventasCount: ventas.length });
        setRecentVentas(ventas.slice(0, 5));

        // Experiencias próximas — solo admin
        if (isAdmin) {
          const expSnap = await getDocs(collection(db, 'experiencias'));
          if (cancelled) return;
          const hoy = new Date();
          const en15 = addDays(hoy, 15);
          const prox = [];
          expSnap.forEach(d => {
            const e = d.data();
            if (!e.fecha) return;
            const fecha = e.fecha?.toDate ? e.fecha.toDate() : new Date(e.fecha);
            if (isAfter(fecha, hoy) && isBefore(fecha, en15))
              prox.push({ id: d.id, ...e, fechaDate: fecha });
          });
          prox.sort((a, b) => a.fechaDate - b.fechaDate);
          setProxExperiencias(prox.slice(0, 3));
        }
      } catch (err) { console.error(err); }
      if (!cancelled) setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [userData, isAdmin]);

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>{format(new Date(), "EEEE d 'de' MMMM", { locale: es })}</p>
      </div>
      <div className="page-body">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">{isAdmin ? 'Ventas totales' : 'Mis ventas'}</div>
            <div className="stat-value">${stats.totalVentas.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
            <div className="stat-sub">{stats.ventasCount} operaciones</div>
          </div>
          {isAdmin && (
            <div className="stat-card">
              <div className="stat-label">Ingreso Floreciendo</div>
              <div className="stat-value">${stats.totalMalaika.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
              <div className="stat-sub">Después de comisiones</div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-label">{isAdmin ? 'Total comisiones' : 'Mi comisión'}</div>
            <div className="stat-value">${stats.totalComisiones.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</div>
            <div className="stat-sub">20% sobre ventas</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Alertas stock</div>
            <div className="stat-value" style={{ color: alertsStock.length > 0 ? 'var(--alert-red)' : 'var(--forest)' }}>
              {alertsStock.length}
            </div>
            <div className="stat-sub">Productos a reponer</div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Alertas stock */}
          <div className="card">
            <div className="card-title">⚡ Alertas de Stock</div>
            {alertsStock.length === 0 ? (
              <div className="empty-state" style={{ padding: '20px 0' }}>
                <div className="empty-state-icon">✓</div>
                <p>Sin alertas de stock</p>
              </div>
            ) : alertsStock.map(a => (
              <div key={a.id} className={`alert-item ${a.stockDisponible === 0 ? 'alert-urgent' : 'alert-warning'}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{a.producto}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                    {a.aroma}{isAdmin ? ` · ${a.embajadoraNombre}` : ''}
                  </div>
                </div>
                <span className={`badge ${a.stockDisponible === 0 ? 'badge-red' : 'badge-yellow'}`}>
                  Stock: {a.stockDisponible}
                </span>
              </div>
            ))}
            {alertsStock.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setActive('stock')}>
                Ver todo el stock →
              </button>
            )}
          </div>

          {/* Próximas experiencias — solo admin */}
          {isAdmin && (
            <div className="card">
              <div className="card-title">◆ Próximas Experiencias</div>
              {proxExperiencias.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}>
                  <div className="empty-state-icon">▦</div>
                  <p>Sin experiencias en los próximos 15 días</p>
                </div>
              ) : proxExperiencias.map(e => (
                <div key={e.id} className="alert-item alert-info" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{e.nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                    {format(e.fechaDate, "EEEE d 'de' MMMM · HH:mm", { locale: es })}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    {e.participantes?.length || 0} participantes · ${(e.ingresos || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                  </div>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setActive('calendario')}>
                Ver calendario →
              </button>
            </div>
          )}

          {/* Ventas recientes — cada una ve las suyas */}
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-title">◎ {isAdmin ? 'Ventas Recientes' : 'Mis Ventas Recientes'}</div>
            {recentVentas.length === 0 ? (
              <div className="empty-state" style={{ padding: '20px 0' }}>
                <p>Sin ventas registradas aún</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      {isAdmin && <th>Embajadora</th>}
                      <th>Producto</th>
                      <th>Cliente</th>
                      <th>Cant.</th>
                      <th>Total</th>
                      <th>Comisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentVentas.map(v => (
                      <tr key={v.id}>
                        <td>{v.fecha ? format(v.fecha.toDate ? v.fecha.toDate() : new Date(v.fecha), 'dd/MM/yy') : '-'}</td>
                        {isAdmin && <td>{v.embajadoraNombre}</td>}
                        <td style={{ fontWeight: 500 }}>{v.producto}</td>
                        <td style={{ color: 'var(--text-light)' }}>{v.cliente || '-'}</td>
                        <td>{v.cantidad || 1}</td>
                        <td>${(v.precio || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</td>
                        <td style={{ color: 'var(--forest)' }}>${(v.comision || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setActive('ventas')}>
              Ver todas →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
